import React from 'react';
import { useAppContext, View } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { triggerHaptic } from '../../utils/haptics';
import { useGroupChatUnreadCount } from '../../hooks/useGroupChat';
import { useMatchmakingBadges } from '../../hooks/useMatchmakingBadges';
import { useTranslation } from 'react-i18next';

export const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const { view, goToPage, currentGroup } = useAppContext();
  const { user } = useAuth();


  // Count unread chat messages
  const chatUnreadCount = useGroupChatUnreadCount(currentGroup?.id);

  const pendingInvitationsCount = React.useMemo(() => {
    if (!currentGroup || !user) return 0;
    let count = 0;
    (currentGroup.tournaments || []).forEach(t => {
      if (!t.teams) return;
      const pending = t.teams.filter(team => team.player2Id === user.uid && team.player2Status === 'pending');
      count += pending.length;
    });
    return count;
  }, [currentGroup, user]);

  // Count actionable matchmaking items
  const { totalBadges: matchmakingUnreadCount } = useMatchmakingBadges(currentGroup, user?.uid);

  type NavItem = {
    view: View;
    icon: string;
    label: string;
    onClick?: () => void;
    badge?: number;
  };

  const navItems: NavItem[] = [
    { view: View.HOME, icon: 'home', label: t('navbar.home', 'Accueil') },
    {
      view: View.TOURNAMENTS,
      icon: 'emoji_events',
      label: t('navbar.tournaments', 'Tournois'),
      badge: pendingInvitationsCount > 0 ? pendingInvitationsCount : undefined
    },
    { 
      view: View.MATCHMAKING, 
      icon: 'handshake', 
      label: t('navbar.matchmaking', 'MatchMaking'),
      badge: matchmakingUnreadCount > 0 ? matchmakingUnreadCount : undefined
    },
    { view: View.PLAYERS, icon: 'groups', label: t('navbar.ranking', 'Classement') },
  ];

  // Chat Button — inserted BEFORE admin for admins, AFTER classement for players
  if (currentGroup) {
    navItems.push({
      view: View.GROUP_CHAT,
      icon: 'forum',
      label: t('navbar.chat', 'Chat'),
      // Ne montrer le badge que si l'utilisateur n'est pas DÉJÀ sur la vue chat
      badge: (view !== View.GROUP_CHAT && chatUnreadCount > 0) ? chatUnreadCount : undefined,
    });
  }

  // Contextual Admin Button Removed (moved to GroupHeader for space)


    const getNavStyles = (targetView: View) => {
      const isActive = view === targetView ||
        (targetView === View.TOURNAMENTS && (view === View.TOURNAMENT_DETAIL || view === View.CREATE_TOURNAMENT || view === View.BRACKET)) ||
        (targetView === View.GROUP_ADMIN && view === View.GROUP_ADMIN) ||
        (targetView === View.GROUP_CHAT && view === View.GROUP_CHAT);
  
      let iconColor = 'text-primary'; // Default for HOME
      
      if (targetView === View.TOURNAMENTS) {
          iconColor = isActive ? 'text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'text-yellow-600/70 group-hover:text-yellow-400';
      } else if (targetView === View.GROUP_CHAT) {
          iconColor = isActive ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]' : 'text-blue-600/70 group-hover:text-blue-400';
      } else if (targetView === View.MATCHMAKING) {
          iconColor = isActive ? 'text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'text-emerald-600/70 group-hover:text-emerald-400';
      } else if (targetView === View.PLAYERS) {
          iconColor = isActive ? 'text-purple-400 drop-shadow-[0_0_8px_rgba(192,132,252,0.5)]' : 'text-purple-600/70 group-hover:text-purple-400';
      } else {
          iconColor = isActive ? 'text-primary drop-shadow-[0_0_8px_rgba(168,206,128,0.5)]' : 'text-text-secondary group-hover:text-primary';
      }
  
      return {
          container: `flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-2 transition-all duration-300 group ${isActive ? 'text-text-primary' : 'text-text-secondary'} hover:text-text-primary`,
          icon: iconColor
      };
    };

  const navbarClasses = "fixed bottom-0 left-0 right-0 h-16 pt-1 safe-area-pb bg-[#0f111a] border-t border-glass-border-light dark:border-glass-border-dark z-50 lg:hidden shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]";

  return (
    <nav className={navbarClasses}>
      <div className="flex justify-evenly items-center h-full max-w-lg mx-auto px-1 relative">
        {navItems.map((item: any) => {
          const styles = getNavStyles(item.view);
          return (
          <button
            key={item.label} // unique enough
            className={styles.container}
            onClick={() => {
              triggerHaptic();
              if (item.onClick) {
                item.onClick();
              } else {
                goToPage(item.view);
              }
            }}
          >
            <span className={`material-symbols-outlined text-[22px] relative transition-colors ${styles.icon}`}>
              {item.icon}
              {item.badge && item.badge > 0 && (
                <span className="absolute -top-1.5 -right-2 bg-gradient-to-br from-red-500 to-rose-600 text-white text-[10px] font-rajdhani font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 border-[2px] border-surface-1-light dark:border-surface-1-dark shadow-sm z-10">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium leading-tight truncate">{item.label}</span>
          </button>
        )})}
      </div>
    </nav>
  );
};